import { useEffect, useMemo, useState } from "react";
import { subDays, subMonths } from "date-fns";
import { t } from "@/i18n-js/instance";
import { NoPostsState } from "@/react/components/Spaces/AiSummary/NoPostsState";
import { AiSummary } from "@/react/components/shared/AiSummary";
import { getShortDateRange } from "@/react/helpers/dateTimeHelpers/getLocalizedRange";
import {
  SPACE_DATE_RANGE,
  type SpaceDateRange,
} from "@/react/types/AiSummary/SpaceSummary";
import { SpaceSummaryContent } from "@circle-react/components/Spaces/AiSummary/SummaryContent";
import {
  FADE_OUT_DURATION,
  getAnimationDelay,
} from "@circle-react/components/Spaces/AiSummary/animations";
import { RailBarPortal } from "@circle-react/components/shared/AiSummary/RailBarPortal";
import { toLocaleString } from "@circle-react/helpers/number";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { Source } from "@circle-react/types/AiSummary/Source";
import { Rail } from "@circle-react-uikit/Rail";
import { useFeedSummary } from "../../hooks/useFeedSummary";

interface FeedSummaryProps {
  dateRange: SpaceDateRange;
  isOpen: boolean;
  onClose: () => void;
  sourceName: string;
}

export const FeedSummary = ({
  isOpen,
  onClose,
  dateRange,
  sourceName,
}: FeedSummaryProps) => {
  const dateFrom = useMemo(() => {
    const now = new Date();
    return dateRange === SPACE_DATE_RANGE.LAST_7_DAYS
      ? subDays(now, 7).toISOString()
      : subMonths(now, 1).toISOString();
  }, [dateRange]);

  const { spaceSummary, isLoadingSpaceSummary, isNoPostsError } =
    useFeedSummary({
      dateFrom,
      enabled: isOpen,
      dateRange,
    });

  const [isFadingOut, setIsFadingOut] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const formattedDateRange = getShortDateRange(new Date(dateFrom), new Date());

  useEffect(() => {
    // Reset animation state when dateRange changes
    setAnimationKey(prev => prev + 1);
  }, [dateRange]);

  const handleCollapseClick = (): void => {
    setIsFadingOut(true);
    setTimeout(() => {
      setIsFadingOut(false);
      onClose();
    }, FADE_OUT_DURATION);
  };

  const selectorQuery = `#rail-bar-header`;
  const portalElement = document.querySelector(selectorQuery);
  if (!portalElement) {
    return null;
  }
  const { topics = [] } = spaceSummary || {};
  const events = spaceSummary?.events_overview || "";

  const allSources = topics
    .flatMap(topic => topic.relevant_posts)
    .filter((source): source is Source => source !== undefined);

  const totalRecords = spaceSummary?.total_records;

  return (
    <RailBarPortal shouldShowRailBar={isOpen}>
      <Rail.Content heightGap={16}>
        <div
          className={classNames("transition-opacity duration-300", {
            "opacity-0": isFadingOut,
            "opacity-100": !isFadingOut,
          })}
        >
          <div className="bg-ai-summary absolute inset-0 bg-cover bg-center bg-no-repeat" />
          {isNoPostsError && !isLoadingSpaceSummary ? (
            <NoPostsState onCollapseClick={handleCollapseClick} />
          ) : (
            <div className="relative" key={animationKey}>
              <AiSummary onCollapseClick={handleCollapseClick}>
                <SpaceSummaryContent
                  spaceSummary={spaceSummary}
                  sourceName={sourceName}
                  formattedDateRange={formattedDateRange}
                  isLoadingSpaceSummary={isLoadingSpaceSummary}
                  allSources={allSources}
                  topics={topics}
                  events={events}
                />
                {typeof totalRecords === "number" && totalRecords > 0 && (
                  <AiSummary.Footer>
                    <div
                      className="animate-fade-in-slow opacity-0"
                      style={{
                        animationDelay: getAnimationDelay(5, topics.length),
                        animationFillMode: "forwards",
                      }}
                    >
                      <AiSummary.FooterNote>
                        {t("ai_spaces_summaries.posts_summarized", {
                          count: totalRecords,
                          formatted_count: toLocaleString(totalRecords),
                        })}
                      </AiSummary.FooterNote>
                    </div>
                  </AiSummary.Footer>
                )}
              </AiSummary>
            </div>
          )}
        </div>
      </Rail.Content>
    </RailBarPortal>
  );
};
